import React, {
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import {
	Phone,
	PhoneCall,
	PhoneOff,
	PhoneIncoming,
	Mic,
	MicOff,
	X,
	Delete,
	GripVertical,
} from "lucide-react";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import useSegurar from "../../hooks/useSegurar";
import { useBotoesOcultos } from "../../ligacoes/botoesFlutuantes";
import OcultarBotoesLigacao from "../OcultarBotoesLigacao";
import {
	useSoftphone,
	configurar,
	desconfigurar,
	setAudioEl,
	abrir,
	fechar,
	setNumero,
	ligarPara,
	atender,
	rejeitar,
	encerrar,
	mutar,
	digitar,
	apagar,
} from "../../softphone/store";

// Widget flutuante do softphone. Le a config de VoIP da empresa (Settings) e o
// ramal/senha do usuario, registra no PBX e mostra o discador. Todo o estado
// vem do store compartilhado, entao os botoes no cabecalho do ticket falam com
// o mesmo ramal.

const SUFIXO_SIP = "npvoice.com.br";
const WSS_PADRAO = "wss://ws.npvoice.com.br";

// Onde o usuario largou o telefone. Guardado no navegador para cada pessoa
// deixar o botao no canto que preferir, sem atrapalhar a barra de mensagem.
const CHAVE_POS = "softphone_pos";

const rotuloReg = {
	registered: "Online",
	connecting: "Conectando…",
	unregistered: "Offline",
	failed: "Falhou",
};
const corReg = {
	registered: "bg-emerald-500",
	connecting: "bg-amber-500",
	unregistered: "bg-gray-400",
	failed: "bg-red-500",
};

const teclas = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

const Softphone = () => {
	const { user, isAuth } = useContext(AuthContext);
	const sp = useSoftphone();
	const audioRef = useRef(null);
	const [segundos, setSegundos] = useState(0);
	const timerRef = useRef(null);

	// Posicao arrastavel do widget (canto superior esquerdo, em px). `null` =
	// posicao padrao (canto inferior direito, via classes). O ref acompanha o
	// valor para os handlers de ponteiro lerem sem depender do fechamento.
	const [pos, setPos] = useState(() => {
		try {
			const s = localStorage.getItem(CHAVE_POS);
			return s ? JSON.parse(s) : null;
		} catch {
			return null;
		}
	});
	const posRef = useRef(pos);
	const widgetRef = useRef(null);
	const arraste = useRef(null);
	const ocultos = useBotoesOcultos();
	const [pedirOcultar, setPedirOcultar] = useState(false);
	const segurar = useSegurar(() => {
		arraste.current = null;
		setPedirOcultar(true);
	});

	const aplicarPos = (np) => {
		posRef.current = np;
		setPos(np);
	};

	// Mantem o widget inteiro dentro da tela, com uma folga de 8px.
	const limites = (x, y, w, h) => ({
		x: Math.max(8, Math.min(x, window.innerWidth - w - 8)),
		y: Math.max(8, Math.min(y, window.innerHeight - h - 8)),
	});

	// Registra o elemento de audio no store (o remoteStream toca aqui).
	useEffect(() => {
		setAudioEl(audioRef.current);
		return () => setAudioEl(null);
	}, []);

	// Carrega a config e configura o ramal.
	useEffect(() => {
		if (!isAuth || !user?.id) return;
		let cancelado = false;
		(async () => {
			try {
				const [resSettings, resUser] = await Promise.all([
					api.get("/settings"),
					api.get(`/users/${user.id}`),
				]);
				if (cancelado) return;
				const get = (k) =>
					(resSettings.data || []).find((s) => s.key === k)?.value || "";
				const empresa = get("voipSipEmpresa").trim();
				const wssUrl = (get("voipWssUrl") || WSS_PADRAO).trim();
				const ramal = (resUser.data?.ramal || "").trim();
				const senha = resUser.data?.ramalSenha || "";
				if (!ramal || !empresa) {
					desconfigurar();
					return;
				}
				configurar({
					wssUrl,
					domain: `${empresa}.${SUFIXO_SIP}`,
					extension: ramal,
					password: senha,
					displayName: resUser.data?.name || ramal,
				});
			} catch {
				desconfigurar();
			}
		})();
		return () => {
			cancelado = true;
		};
	}, [isAuth, user]);

	// Cronometro da chamada ativa.
	useEffect(() => {
		if (sp.callState === "active") {
			setSegundos(0);
			timerRef.current = setInterval(() => setSegundos((s) => s + 1), 1000);
		} else {
			clearInterval(timerRef.current);
		}
		return () => clearInterval(timerRef.current);
	}, [sp.callState]);

	// O <audio> existe sempre (mesmo sem ramal) para o store ter onde tocar.
	const emChamada = sp.callState !== "idle";
	const tocandoEntrada = sp.callState === "ringing-in";
	const mmss = `${String(Math.floor(segundos / 60)).padStart(2, "0")}:${String(
		segundos % 60,
	).padStart(2, "0")}`;

	// Sempre que o widget muda de tamanho (abrir/fechar, chamada entrando), o
	// que cabia como botao pode nao caber como painel: reencaixa na tela.
	useLayoutEffect(() => {
		if (!posRef.current || !widgetRef.current) return;
		const r = widgetRef.current.getBoundingClientRect();
		const c = limites(posRef.current.x, posRef.current.y, r.width, r.height);
		if (c.x !== posRef.current.x || c.y !== posRef.current.y) aplicarPos(c);
	}, [pos, sp.aberto, sp.callState, tocandoEntrada]);

	// Se a janela encolhe, um widget largado no canto pode ficar fora da tela.
	useEffect(() => {
		const aoRedimensionar = () => {
			if (!posRef.current || !widgetRef.current) return;
			const r = widgetRef.current.getBoundingClientRect();
			aplicarPos(limites(posRef.current.x, posRef.current.y, r.width, r.height));
		};
		window.addEventListener("resize", aoRedimensionar);
		return () => window.removeEventListener("resize", aoRedimensionar);
	}, []);

	// Arrastar: guarda o offset do clique dentro do widget e vai movendo. Um
	// gesto que anda menos de 3px conta como clique, nao como arraste.
	const iniciarArraste = (e) => {
		const el = widgetRef.current;
		if (!el) return;
		const r = el.getBoundingClientRect();
		arraste.current = {
			startX: e.clientX,
			startY: e.clientY,
			dx: e.clientX - r.left,
			dy: e.clientY - r.top,
			w: r.width,
			h: r.height,
			moveu: false,
		};
		el.setPointerCapture?.(e.pointerId);
		segurar.iniciar();
	};
	const moverArraste = (e) => {
		const a = arraste.current;
		if (!a) return;
		if (Math.hypot(e.clientX - a.startX, e.clientY - a.startY) > 3) {
			a.moveu = true;
			segurar.cancelar();
		}
		aplicarPos(limites(e.clientX - a.dx, e.clientY - a.dy, a.w, a.h));
	};
	const soltarArraste = (e, aoClicar) => {
		segurar.cancelar();
		const a = arraste.current;
		arraste.current = null;
		if (!a) return;
		if (a.moveu) {
			try {
				localStorage.setItem(CHAVE_POS, JSON.stringify(posRef.current));
			} catch {
				/* navegador sem storage: fica so nesta sessao */
			}
		} else if (aoClicar) {
			aoClicar();
		}
	};

	const estiloPos = pos
		? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
		: undefined;

	return (
		<>
			<audio ref={audioRef} autoPlay />

			<OcultarBotoesLigacao
				aberto={pedirOcultar}
				aoFechar={() => setPedirOcultar(false)}
			/>

			{sp.conta && !sp.aberto && (!ocultos || emChamada) && (
				<button
					ref={widgetRef}
					type="button"
					onPointerDown={iniciarArraste}
					onPointerMove={moverArraste}
					onPointerUp={(e) => soltarArraste(e, abrir)}
					aria-label="Abrir telefone (arraste para mover)"
					title="Clique para abrir · arraste para mover"
					style={estiloPos}
					onContextMenu={(e) => e.preventDefault()}
					className={`fixed z-40 flex h-12 w-12 touch-none select-none cursor-grab items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg active:cursor-grabbing ${
						pos ? "" : "bottom-24 right-4"
					}`}
				>
					<Phone className="h-5 w-5" />
					<span
						className={`absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-background ${corReg[sp.regState]}`}
					/>
				</button>
			)}

			{sp.conta && sp.aberto && (
				<div
					ref={widgetRef}
					style={estiloPos}
					className={`fixed z-40 w-72 rounded-xl border bg-card p-4 shadow-2xl ${
						pos ? "" : "bottom-24 right-4"
					}`}
				>
					<div className="mb-3 flex items-center justify-between">
						<div className="flex items-center gap-2 text-sm font-medium">
							<span
								onPointerDown={iniciarArraste}
								onPointerMove={moverArraste}
								onPointerUp={(e) => soltarArraste(e)}
								title="Arraste para mover"
								className="-ml-1 cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
							>
								<GripVertical className="h-4 w-4" />
							</span>
							<span className={`h-2.5 w-2.5 rounded-full ${corReg[sp.regState]}`} />
							{rotuloReg[sp.regState] || sp.regState}
							<span className="text-xs text-muted-foreground">
								· ramal {sp.conta.extension}
							</span>
						</div>
						<button
							type="button"
							onClick={fechar}
							aria-label="Fechar"
							className="text-muted-foreground hover:text-foreground"
						>
							<X className="h-4 w-4" />
						</button>
					</div>

					{tocandoEntrada && (
						<div className="mb-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-center dark:border-emerald-800 dark:bg-emerald-950">
							<PhoneIncoming className="mx-auto mb-1 h-5 w-5 text-emerald-600" />
							<p className="text-sm font-medium">
								{sp.incoming?.displayName || sp.incoming?.number || "Chamada"}
							</p>
							<p className="mb-2 text-xs text-muted-foreground">
								{sp.incoming?.number}
							</p>
							<div className="flex justify-center gap-2">
								<button
									type="button"
									onClick={atender}
									className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white"
								>
									<PhoneCall className="h-4 w-4" /> Atender
								</button>
								<button
									type="button"
									onClick={rejeitar}
									className="flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-sm text-white"
								>
									<PhoneOff className="h-4 w-4" /> Recusar
								</button>
							</div>
						</div>
					)}

					{sp.callState === "active" && (
						<div className="mb-3 rounded-lg bg-muted p-3 text-center">
							<p className="text-sm font-medium">Em chamada</p>
							<p className="font-mono text-lg tabular-nums">{mmss}</p>
						</div>
					)}

					{!tocandoEntrada && (
						<div className="mb-2 flex items-center gap-2">
							<input
								value={sp.numero}
								onChange={(e) => setNumero(e.target.value)}
								placeholder="Numero ou ramal"
								inputMode="tel"
								className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
							/>
							{!emChamada && sp.numero && (
								<button
									type="button"
									onClick={apagar}
									aria-label="Apagar"
									className="text-muted-foreground hover:text-foreground"
								>
									<Delete className="h-4 w-4" />
								</button>
							)}
						</div>
					)}

					<div className="mb-3 grid grid-cols-3 gap-1.5">
						{teclas.map((t) => (
							<button
								key={t}
								type="button"
								onClick={() => digitar(t)}
								className="rounded-lg border py-2 text-sm font-medium hover:bg-muted"
							>
								{t}
							</button>
						))}
					</div>

					<div className="flex items-center justify-center gap-2">
						{emChamada ? (
							<>
								<button
									type="button"
									onClick={mutar}
									aria-label={sp.muted ? "Desmutar" : "Mutar"}
									className={`flex h-11 w-11 items-center justify-center rounded-full border ${
										sp.muted ? "bg-amber-100 dark:bg-amber-950" : ""
									}`}
								>
									{sp.muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
								</button>
								<button
									type="button"
									onClick={encerrar}
									aria-label="Encerrar"
									className="flex h-11 w-11 items-center justify-center rounded-full bg-red-600 text-white"
								>
									<PhoneOff className="h-5 w-5" />
								</button>
							</>
						) : (
							<button
								type="button"
								onClick={() => ligarPara(sp.numero.replace(/\s+/g, ""))}
								disabled={sp.regState !== "registered" || !sp.numero}
								aria-label="Ligar"
								className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-600 text-white disabled:opacity-40"
							>
								<PhoneCall className="h-5 w-5" />
							</button>
						)}
					</div>
				</div>
			)}
		</>
	);
};

export default Softphone;
